import { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Download, FileText, Code2, ExternalLink, Loader2, FileCode, BookPlus, FolderInput, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useContractsStore } from '@/store/contracts';
import { useAppStore } from '@/store';
import { useAuthStore } from '@/store/auth';
import { getContractDocxBlob, downloadContractDocx } from '@/services/contractsApi';
import { uploadTemplate } from '@/services/api';

const STATUS_COLOR: Record<string, string> = {
  'Чинний': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Втратив чинність': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Втратив чинність частково': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Не набрав чинності': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

function processContractHtml(html: string): string {
  return html
    .replace(/­/g, '')  // strip soft hyphens (appear as "n" artifacts in some fonts)
    .replace(/href="\/document\/view\/([^"?]+)[^"]*"/g, (_, docId) => {
      return `href="https://zakon.rada.gov.ua/laws/show/${docId}" target="_blank" rel="noopener noreferrer"`;
    })
    .replace(/href="\/laws\/show\/([^"]+)"/g, (_, path) => {
      return `href="https://zakon.rada.gov.ua/laws/show/${path}" target="_blank" rel="noopener noreferrer"`;
    });
}

function preprocessMarkdown(md: string): string {
  return md.replace(/­/g, '');  // strip soft hyphens that appear as "n nn n" artifacts
}

type ActionState = 'idle' | 'loading' | 'done' | 'error';

export function ContractViewer() {
  const { activeContract, loadingDetail, displayFormat, setDisplayFormat, closeContract } = useContractsStore();
  const activeCaseId = useAppStore((s) => s.activeCaseId);
  const uploadFiles = useAppStore((s) => s.uploadFiles);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [downloading, setDownloading] = useState(false);
  const [templateState, setTemplateState] = useState<ActionState>('idle');
  const [caseState, setCaseState] = useState<ActionState>('idle');
  const htmlRef = useRef<HTMLDivElement>(null);

  const handleDocx = useCallback(async () => {
    if (!activeContract) return;
    setDownloading(true);
    try {
      await downloadContractDocx(activeContract.id);
    } finally {
      setDownloading(false);
    }
  }, [activeContract]);

  const handleAddAsTemplate = useCallback(async () => {
    if (!activeContract || templateState === 'loading') return;
    setTemplateState('loading');
    try {
      const { blob, filename } = await getContractDocxBlob(activeContract.id);
      const file = new File([blob], filename, { type: blob.type });
      await uploadTemplate(file);
      setTemplateState('done');
      setTimeout(() => setTemplateState('idle'), 3000);
    } catch (err) {
      console.error('addAsTemplate error', err);
      setTemplateState('error');
      setTimeout(() => setTemplateState('idle'), 2000);
    }
  }, [activeContract, templateState]);

  const handleAddToCase = useCallback(async () => {
    if (!activeContract || !activeCaseId || caseState === 'loading') return;
    setCaseState('loading');
    try {
      const { blob, filename } = await getContractDocxBlob(activeContract.id);
      const file = new File([blob], filename, { type: blob.type });
      await uploadFiles([file]);
      setCaseState('done');
      setTimeout(() => setCaseState('idle'), 3000);
    } catch (err) {
      console.error('addToCase error', err);
      setCaseState('error');
      setTimeout(() => setCaseState('idle'), 2000);
    }
  }, [activeContract, activeCaseId, caseState, uploadFiles]);

  // Make HTML links open in new tab
  useEffect(() => {
    if (displayFormat !== 'html' || !htmlRef.current) return;
    const links = htmlRef.current.querySelectorAll('a[href]');
    links.forEach((a) => {
      (a as HTMLAnchorElement).target = '_blank';
      (a as HTMLAnchorElement).rel = 'noopener noreferrer';
    });
  }, [activeContract, displayFormat]);

  if (loadingDetail) {
    return (
      <div className="flex-1 flex items-center justify-center text-white/30">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!activeContract) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-white/20 gap-3 p-8">
        <FileText className="w-12 h-12 opacity-30" />
        <p className="text-sm text-center">Оберіть документ зі списку, щоб переглянути його зміст</p>
      </div>
    );
  }

  const processedHtml = activeContract.full_html ? processContractHtml(activeContract.full_html) : null;
  const cleanMarkdown = activeContract.full_markdown ? preprocessMarkdown(activeContract.full_markdown) : null;

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0">
      {/* Header */}
      <div className="shrink-0 border-b border-white/10 bg-[var(--t-panel)] px-5 py-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-white leading-snug line-clamp-2">
              {activeContract.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {activeContract.status_name && (
                <span className={cn('text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border',
                  STATUS_COLOR[activeContract.status_name] ?? 'bg-white/10 text-white/40 border-white/10')}>
                  {activeContract.status_name}
                </span>
              )}
              {activeContract.type_doc_name && (
                <span className="text-[10px] text-white/40 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                  {activeContract.type_doc_name}
                </span>
              )}
              {activeContract.date_accept_iso && (
                <span className="text-[10px] text-white/30">
                  {new Date(activeContract.date_accept_iso).toLocaleDateString('uk-UA')}
                </span>
              )}
              {activeContract.ref_doc_id && (
                <a
                  href={`https://zakon.rada.gov.ua/laws/show/${activeContract.ref_doc_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  Підстава: {activeContract.ref_doc_id}
                </a>
              )}
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
            {/* Add to templates (admin only) */}
            {isAdmin && (
              <button
                onClick={handleAddAsTemplate}
                disabled={templateState === 'loading'}
                title="Додати документ до системних шаблонів"
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] font-medium transition-colors',
                  templateState === 'done'
                    ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30'
                    : templateState === 'error'
                    ? 'bg-red-600/20 text-red-300 border-red-500/30'
                    : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                )}
              >
                {templateState === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : templateState === 'done' ? <Check className="w-3.5 h-3.5" /> : <BookPlus className="w-3.5 h-3.5" />}
                {templateState === 'done' ? 'Додано!' : templateState === 'error' ? 'Помилка' : 'У шаблони'}
              </button>
            )}

            {/* Add to active case */}
            <button
              onClick={handleAddToCase}
              disabled={!activeCaseId || caseState === 'loading'}
              title={!activeCaseId ? 'Відкрийте справу в розділі Чат' : 'Додати документ до поточної справи'}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] font-medium transition-colors',
                !activeCaseId
                  ? 'opacity-40 cursor-not-allowed bg-white/5 border-white/10 text-white/40'
                  : caseState === 'done'
                  ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30'
                  : caseState === 'error'
                  ? 'bg-red-600/20 text-red-300 border-red-500/30'
                  : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
              )}
            >
              {caseState === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : caseState === 'done' ? <Check className="w-3.5 h-3.5" /> : <FolderInput className="w-3.5 h-3.5" />}
              {caseState === 'done' ? 'Додано!' : caseState === 'error' ? 'Помилка' : 'До справи'}
            </button>

            {/* Format toggle */}
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5 text-[11px]">
              <button
                onClick={() => setDisplayFormat('markdown')}
                title="Markdown"
                className={cn('flex items-center gap-1 px-2 py-1 rounded transition-colors',
                  displayFormat === 'markdown' ? 'bg-blue-600 text-white' : 'text-white/50 hover:text-white')}
              >
                <FileCode className="w-3.5 h-3.5" />
                MD
              </button>
              <button
                onClick={() => setDisplayFormat('html')}
                title="HTML"
                className={cn('flex items-center gap-1 px-2 py-1 rounded transition-colors',
                  displayFormat === 'html' ? 'bg-blue-600 text-white' : 'text-white/50 hover:text-white')}
              >
                <Code2 className="w-3.5 h-3.5" />
                HTML
              </button>
            </div>

            <button
              onClick={handleDocx}
              disabled={downloading}
              title="Завантажити DOCX"
              className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </button>

            <button
              onClick={closeContract}
              title="Закрити"
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
        {displayFormat === 'markdown' && cleanMarkdown ? (
          <div className="prose prose-invert prose-sm max-w-none
            prose-headings:text-white prose-headings:font-semibold
            prose-p:text-white/80 prose-p:leading-relaxed
            prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white prose-li:text-white/80
            prose-table:text-white/80 prose-th:text-white/60 prose-td:text-white/80
          ">
            <ReactMarkdown
              components={{
                a: ({ href, children }) => {
                  const isExternal = href?.startsWith('http') || href?.startsWith('/document/');
                  const finalHref = href?.startsWith('/document/view/')
                    ? `https://zakon.rada.gov.ua/laws/show/${href.replace('/document/view/', '').split('?')[0]}`
                    : href;
                  return (
                    <a
                      href={finalHref}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      className="text-blue-400 hover:underline"
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {cleanMarkdown}
            </ReactMarkdown>
          </div>
        ) : displayFormat === 'html' && processedHtml ? (
          <div
            ref={htmlRef}
            className="contract-html text-white/80 text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: processedHtml }}
          />
        ) : (
          <div className="text-white/30 text-sm text-center py-8">
            Вміст недоступний для цього документа
          </div>
        )}
      </div>
    </div>
  );
}
